import { Helmet } from 'react-helmet-async';
import { useState } from "react";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  Send,
  CheckCircle
} from "lucide-react";
import { companyInfo } from "../data/mock";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    product: "",
    message: ""
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        // Fire Google Ads conversion tracking
        if (window.gtag) {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-17935401679/Fg0VCKzvwZgcEM-FouhC',
            'value': 1.0,
            'currency': 'INR'
          });
        }
        
        toast.success("Success!", {
          description: response.data.message,
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          product: "",
          message: ""
        });
      }
    } catch (error) {
      toast.error("Error", {
        description: error.response?.data?.detail || "Something went wrong. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-amber-500/20 text-amber-300 border-amber-500/30">Contact Us</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Let's Discuss Your Manufacturing Needs
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Get in touch with our experts for customized machinery solutions, technical support, or any inquiries about our products and services.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-t-4 border-amber-600">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
                    <MapPin className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">Visit Us</h3>
                  <p className="text-slate-600 leading-relaxed">
                    {companyInfo.address}
                  </p>
                  <a
                    href="https://maps.google.com/?q=13.04682800,80.15802000"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-600 hover:text-cyan-700 text-sm mt-2 inline-block"
                  >
                    Get Directions →
                  </a>
                </CardContent>
              </Card>

              <Card className="border-t-4 border-blue-600">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <Phone className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">Call Us</h3>
                  <a
                    href={`tel:${companyInfo.phone}`}
                    className="text-slate-600 hover:text-blue-600 block mb-2 transition-colors"
                  >
                    {companyInfo.phone}
                  </a>
                  <p className="text-sm text-slate-500">
                    Monday - Saturday: 9:00 AM - 6:00 PM
                  </p>
                </CardContent>
              </Card>

              <Card className="border-t-4 border-purple-600">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                    <Mail className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">Email Us</h3>
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="text-slate-600 hover:text-purple-600 block transition-colors break-all"
                  >
                    {companyInfo.email}
                  </a>
                  <p className="text-sm text-slate-500 mt-2">
                    We'll respond within 24 hours
                  </p>
                </CardContent>
              </Card>

              <Card className="border-t-4 border-green-600">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <Phone className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">WhatsApp Support</h3>
                  <a
                    href={`https://wa.me/${companyInfo.whatsapp.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-600 hover:text-green-600 block mb-2 transition-colors"
                  >
                    {companyInfo.whatsapp}
                  </a>
                  <p className="text-sm text-slate-500">
                    Quick response via WhatsApp
                  </p>
                </CardContent>
              </Card>

              <Card className="border-t-4 border-green-600">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <Clock className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">Business Hours</h3>
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex justify-between">
                      <span>Monday - Saturday:</span>
                      <span className="font-medium">9:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sunday:</span>
                      <span className="font-medium">Closed</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-2">
                      Closed on Indian public holidays
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <div className="text-xs text-slate-500 mb-1">24/7 Helpline</div>
                    <a
                      href={`tel:${companyInfo.phone}`}
                      className="text-green-600 font-semibold hover:text-green-700"
                    >
                      {companyInfo.phone}
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="shadow-xl">
                <CardContent className="p-8">
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">
                      Request a Quote
                    </h2>
                    <p className="text-slate-600">
                      Fill out the form below and our team will get back to you with a detailed quotation.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@company.com"
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+91-XXXXXXXXXX"
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label htmlFor="company">Company Name</Label>
                        <Input
                          id="company"
                          name="company"
                          type="text"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Your Company Ltd."
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="product">Product Interest</Label>
                      <Input
                        id="product"
                        name="product"
                        type="text"
                        value={formData.product}
                        onChange={handleChange}
                        placeholder="e.g., BOPP Tape Coating Machine"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message">Message / Requirements *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Please describe your requirements, production capacity, or any specific questions..."
                        rows={6}
                        className="mt-1"
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
                    >
                      {isSubmitting ? (
                        <>Processing...</>
                      ) : (
                        <>
                          Send Message
                          <Send className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>

                    <p className="text-sm text-slate-500 text-center">
                      By submitting this form, you agree to our privacy policy and terms of service.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Find Us on Map</h2>
            <p className="text-slate-600">
              Located in Anna Industrial Estate, Chennai - India's manufacturing hub
            </p>
          </div>
          <div className="rounded-xl overflow-hidden shadow-xl h-96">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.3426734894947!2d80.14780931482214!3d13.012863290825845!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a5260e0c2e0a5c5%3A0x8f3f3f8b3e3f3f3f!2sNo.%2040%2C%20Anna%20Industrial%20Estate%2C%20Mettukuppam%2C%20Vanagaram%2C%20Chennai%2C%20Tamil%20Nadu%20600095!5e0!3m2!1sen!2sin!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Hero Impex Location - Anna Industrial Estate, Chennai"
            />
          </div>
        </div>
      </section>

      {/* Why Contact Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Why Contact Hero Impex?</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <Card>
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Expert Consultation</h3>
                <p className="text-slate-600 text-sm">
                  Get personalized advice from our experienced technical team for your specific requirements.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Competitive Pricing</h3>
                <p className="text-slate-600 text-sm">
                  Receive detailed quotations with transparent pricing and flexible payment options.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Quick Response</h3>
                <p className="text-slate-600 text-sm">
                  Our team responds to all inquiries within 24 hours with comprehensive information.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};
